import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, isProblemError, useApiMutation } from "../../api";
import { Button, Field } from "../../components";
import { destinationSure, homeForRole, useSession } from "../../session";
import { ROUTES } from "../../shell";
import styles from "./Auth.module.css";
import { ChampMotDePasse } from "./ChampMotDePasse";
import { messageConnexion } from "./messages";
import { useCountdown } from "./useCountdown";
import { useLiensTransition, useNavigationTransition } from "../../motion";

/**
 * Écran de connexion.
 *
 * Un seul message d'erreur, quel que soit le refus : l'écran ne dit jamais si
 * l'adresse existe. Le délai d'un 429 ou d'un 423 est affiché et compté à
 * rebours, le bouton reste inactif tant qu'il court.
 */
export function ConnexionPage() {
  const lien = useLiensTransition();
  const { adopt } = useSession();
  const naviguer = useNavigationTransition();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const alerte = useRef<HTMLParagraphElement>(null);

  // L'API d'abord, la session ensuite : la session est posée DANS la
  // transition d'écran (voir `soumettre`), sinon les gardes de route
  // basculeraient vers l'espace avant que la transition ait capturé la
  // connexion.
  const connexion = useApiMutation((e: string, mdp: string) => api.login({ email: e, password: mdp }));
  const retryAfter = isProblemError(connexion.error) ? connexion.error.retryAfter : undefined;
  const restant = useCountdown(retryAfter);

  // L'alerte prend le focus quand elle apparaît : un lecteur d'écran l'annonce,
  // un clavier ne repart pas du haut du formulaire.
  useEffect(() => {
    if (connexion.error !== null) {
      alerte.current?.focus();
    }
  }, [connexion.error]);

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    const me = await connexion.run(email.trim(), motDePasse);
    if (me === null) {
      return;
    }
    const retour = (location.state as { from?: unknown } | null)?.from;
    // L'entrée dans l'espace se fait en « plongée » : la connexion recule,
    // l'application vient vers soi.
    naviguer(destinationSure(retour) ?? homeForRole(me.user.role), "plongee", {
      replace: true,
      avant: () => adopt(me),
    });
  }

  return (
    <>
      <div>
        <h1 className={styles.titre}>Connexion</h1>
        <p className={styles.intro}>Accédez à votre espace avec l'adresse de votre invitation.</p>
      </div>

      <form className={styles.formulaire} onSubmit={(e) => void soumettre(e)} noValidate>
        {connexion.error !== null && (
          <p ref={alerte} className={styles.alerte} role="alert" tabIndex={-1}>
            {messageConnexion(connexion.error, restant)}
          </p>
        )}

        <Field label="Adresse e-mail" required>
          {(commande) => (
            <input
              {...commande}
              type="email"
              autoComplete="username"
              inputMode="email"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={connexion.pending}
            />
          )}
        </Field>

        <ChampMotDePasse
          label="Mot de passe"
          value={motDePasse}
          onChange={setMotDePasse}
          autoComplete="current-password"
          disabled={connexion.pending}
        />

        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={connexion.pending}
          disabled={restant > 0 || email.trim() === "" || motDePasse === ""}
        >
          {restant > 0 ? `Réessayer dans ${restant} s` : "Se connecter"}
        </Button>

        <div className={styles.liens}>
          <Link to={ROUTES.motDePasseOublie} {...lien(ROUTES.motDePasseOublie, "cube-avant")}>Mot de passe oublié ?</Link>
          <Link to={ROUTES.inscription} {...lien(ROUTES.inscription, "cube-avant")}>Créer un compte entreprise</Link>
        </div>
      </form>
    </>
  );
}
